// Phase 3 — accounting pages (Bookkeeping, AP, AR, Cashbook, Payroll, Masters)
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BackToDashBar from "@/components/BackToDashBar";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Loader2, Plus, FileText, DollarSign, BookOpen, Users, Banknote, CheckCircle2, Send, Pencil, Trash2, Archive, RotateCcw, Paperclip, Upload, Download, X, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import { fmtUSD } from "@/lib/tx";
import RecordTrail, { OwnerCell } from "@/components/RecordTrail";
import { AdminTxActions } from "@/components/AdminTxActions";
// Bug bundle #9 (2026-08-11) — reuse the JE edit/reversal sub-modals from
// the Transactions page so the Bookkeeping modal offers the same actions.
import { EditMetadataModal, ReverseAndCorrectModal, CreateJEPanel } from "@/pages/TransactionsPage";
import { Textarea } from "@/components/ui/textarea";

function fmtDate(s) { return s ? (s.length > 10 ? new Date(s).toLocaleDateString() : s) : "—"; }

// ─────────────────────────────────────────────────────────────────────────────
//  BOOKKEEPING
// ─────────────────────────────────────────────────────────────────────────────
export function BookkeepingPage() {
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  // Phase 52-F (2026-08-07) — active tab inside the JE lines dialog.
  const [selectedTab, setSelectedTab] = useState("lines");
  // Bug bundle #9 (2026-08-11) — Part A + B sub-modals.
  const [editMetaOpenJE, setEditMetaOpenJE] = useState(false);
  const [reverseOpenJE, setReverseOpenJE] = useState(false);
  // Bug bundle #11 (2026-08-18) — correction bridge: when the Reverse &
  // Correct modal completes it hands back a `correction_draft`; we open
  // the Create Transaction panel prefilled with those values so the
  // admin can post the correcting entry in one flow.
  const [correctionDraft, setCorrectionDraft] = useState(null);
  // P0 (2026-07-12) — orphan-JE delete action.
  const { can, user } = useAuth();
  const canDeleteJE = can ? can("accounting.bookkeeping.delete_je") : false;
  // Phase 52-D (2026-08-06) — manual JE creation from "+ New JE" modal.
  // Phase 53 (2026-08-08) — RESTRICTED to system_admin only. Non-admin
  // users must submit transactions via the pipeline (which auto-posts
  // a JE on approval). This modal is now the "Adjustment JE" escape
  // hatch for admins only.
  const canCreateJE = (user?.role === "system_admin" || user?.full_access === true)
                       && (can ? can("accounting.bookkeeping.create_je") : false);
  // Phase 52-F (2026-08-07) — narration edit permission (system_admin,
  // senior_accountant, finance_manager by default).
  const canEditNarration = can ? can("accounting.bookkeeping.edit_je_narration") : false;
  const [showNewJE, setShowNewJE] = useState(false);
  // Phase 53 (2026-08-08) — source-type chip filter for the JE list.
  const [jeSourceFilter, setJeSourceFilter] = useState("all");
  const [delTarget, setDelTarget] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  // P0 (2026-07-17) — capture the backend block payload so the delete
  // modal can render an inline "Void payroll run" affordance when the
  // JE is protected by a live source.
  const [delBlock, setDelBlock] = useState(null);
  const [delBlockBusy, setDelBlockBusy] = useState(false);
  // Phase 52-F (2026-08-07) — payroll run status so the delete modal
  // can render a green "voided — safe to delete" banner (and hide the
  // orange retry button) when the paired run is already voided.
  const [delRunStatus, setDelRunStatus] = useState(null);
  // Phase E (2026-10-04) — admin toggle to show JEs soft-voided by the
  // IAS 39 rebuild. Default OFF — Reports/Trial Balance already hide them.
  const [showVoided, setShowVoided] = useState(false);

  const reload = async () => {
    try {
      const url = showVoided
        ? "/journal-entries?page_size=2000&include_voided=true"
        : "/journal-entries?page_size=2000";
      const { data } = await api.get(url);
      setItems(data.items);
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { (async () => { await reload(); setLoading(false); })(); }, [showVoided]);

  const openDelete = (je, e) => {
    e?.stopPropagation?.();
    setDelTarget(je);
    setDelReason("");
    setDelBlock(null);
    setDelRunStatus(null);
    // Phase 52-F (2026-08-07) — pre-fetch payroll run status so the
    // modal can decide whether to hide the "Void payroll run & retry"
    // button and instead surface the green "safe to delete" banner.
    const src = je?.source_type;
    const runId = je?.payroll_run_id
      || (["payroll", "payroll_void"].includes(src) ? je?.source_id : null);
    if (runId) {
      (async () => {
        try {
          const { data } = await api.get(`/payroll-runs/${runId}`);
          setDelRunStatus(data?.status || null);
        } catch (_e) {
          setDelRunStatus(null);
        }
      })();
    }
  };
  const closeDelete = () => { if (delBusy) return; setDelTarget(null); setDelReason(""); setDelBlock(null); setDelRunStatus(null); };
  const confirmDelete = async () => {
    if (!delTarget) return;
    if (delReason.trim().length < 5) {
      toast.error("Please enter a reason (at least 5 characters)."); return;
    }
    setDelBusy(true);
    setDelBlock(null);
    try {
      await api.delete(`/journal-entries/${delTarget.id}`, {
        data: { delete_reason: delReason.trim() },
      });
      toast.success("Journal entry deleted.");
      setDelTarget(null); setDelReason("");
      await reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "live_source_payroll_run") {
        setDelBlock(detail);
      } else if (detail && typeof detail === "object" && detail.error) {
        toast.error(detail.message || "Delete blocked.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally { setDelBusy(false); }
  };

  // P0 (2026-07-17) — one-click "void payroll run then retry JE delete".
  const voidRunAndRetry = async () => {
    if (!delBlock?.payroll_run_id) return;
    setDelBlockBusy(true);
    try {
      await api.post(`/payroll-runs/${delBlock.payroll_run_id}/void`, {
        reason: `Voided from Bookkeeping to allow JE cleanup: ${delReason.trim() || "cleanup"}`,
        reverse_jes: false,
      });
      toast.success("Payroll run voided — retrying JE delete...");
      setDelBlock(null);
      await confirmDelete();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDelBlockBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="bookkeeping-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Bookkeeping — Journal Entries</h2>
          <p className="text-sm text-slate-500 mt-1">All journal entries with debit/credit balances.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Phase E (2026-10-04) — show JEs soft-voided by IAS 39 rebuild. */}
          {(user?.role === "system_admin" || user?.full_access === true) && (
            <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="bookkeeping-show-voided-toggle">
              <input
                type="checkbox"
                checked={showVoided}
                onChange={(e) => setShowVoided(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
                data-testid="bookkeeping-show-voided-cb"
              />
              Show voided-by-rebuild
            </label>
          )}
          {canCreateJE ? (
            <Button
              onClick={() => setShowNewJE(true)}
              data-testid="new-je-btn"
              className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
              title="Admin-only journal entry (bypasses the transaction pipeline)"
            >
              <Plus className="h-4 w-4 mr-2" /> New Journal Entry
            </Button>
          ) : null}
        </div>
      </div>

      {/* Phase 53 (2026-08-08) — filter chips by JE source. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="je-source-chips">
        <span className="text-xs text-slate-500 mr-1">Source:</span>
        {[
          { key: "all",           label: "All" },
          { key: "standard",      label: "Standard (Transaction)" },
          { key: "manual",        label: "Manual" },
          { key: "payroll",       label: "Payroll" },
          { key: "bank_transfer", label: "Bank Transfer" },
          { key: "loan",          label: "Loan" },
        ].map((c) => {
          const active = jeSourceFilter === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setJeSourceFilter(c.key)}
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "bg-[#4FA3D1] text-white border-[#4FA3D1]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              data-testid={`je-source-chip-${c.key}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      <Card><CardContent className="p-0">
        {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
        : (<div className="overflow-x-auto"><Table data-testid="je-table"><TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>Reference</TableHead><TableHead>Narration</TableHead>
          <TableHead>Source</TableHead><TableHead className="text-right">Debit</TableHead>
          <TableHead className="text-right">Credit</TableHead>
          <TableHead className="text-center">Files</TableHead>
          <TableHead></TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader><TableBody>
          {items
            .filter((je) => {
              // Phase 53 source chip filter.
              // Bug bundle #10 (2026-08-18) — reversal JEs are also
              // matched by the chip that fits their ORIGINAL source_type
              // (denormalized as `original_source_type`). This keeps
              // reversal-of-payroll visible under the Payroll chip, etc.
              if (jeSourceFilter === "all") return true;
              const s = je.source_type || "manual";
              const os_ = je.original_source_type || null;
              const inSet = (allowed) =>
                allowed.includes(s) || (s === "reversal" && allowed.includes(os_));
              if (jeSourceFilter === "standard")
                return inSet(["transaction"]);
              if (jeSourceFilter === "manual")
                return inSet(["manual"]);
              if (jeSourceFilter === "payroll")
                return inSet(["payroll", "payroll_void", "payroll_direct"]);
              if (jeSourceFilter === "bank_transfer")
                return inSet(["bank_transfer"]);
              if (jeSourceFilter === "loan")
                return inSet(["loan", "loan_accrual", "loan_payoff"]);
              return true;
            })
            .map((je) => (
            <TableRow
              key={je.id}
              data-testid={`je-row-${je.id}`}
              onClick={() => { setSelected(je); setSelectedTab("lines"); }}
              className={"cursor-pointer hover:bg-slate-50 transition-colors "
                + (je.superseded_by_reversal_je_id ? "opacity-60 " : "")
                + (je.status === "voided_by_rebuild" ? "opacity-50 bg-slate-50 " : "")}
            >
              <TableCell>{fmtDate(je.entry_date)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span>{je.reference || "—"}</span>
                  {/* Phase E (2026-10-04) — voided-by-rebuild badge. */}
                  {je.status === "voided_by_rebuild" && (
                    <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border border-rose-300 text-[9px] px-1.5 py-0" data-testid={`badge-voided-by-rebuild-${je.id}`}>
                      voided_by_rebuild
                    </Badge>
                  )}
                  {/* Bug bundle #9 (2026-08-11) — reversal / superseded / correction badges */}
                  {je.superseded_by_reversal_je_id && (
                    <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-[9px] px-1.5 py-0" data-testid={`badge-superseded-${je.id}`}>
                      Superseded
                    </Badge>
                  )}
                  {je.source_type === "reversal" && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-300 text-[9px] px-1.5 py-0" data-testid={`badge-reversal-${je.id}`}>
                      Reversal
                    </Badge>
                  )}
                  {je.corrects_je_id && (
                    <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 border border-sky-300 text-[9px] px-1.5 py-0"
                           title={`Corrects ${je.corrects_je_id.slice(0, 8)}…`}
                           data-testid={`badge-correction-${je.id}`}>
                      Correction
                    </Badge>
                  )}
                  {(je.metadata_edit_count || 0) > 0 && (
                    <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 border border-amber-200 text-[9px] px-1.5 py-0"
                           title={`Edited ${je.metadata_edit_count} time(s) — last by ${je.metadata_last_edited_by_email || "?"}`}
                           data-testid={`badge-edited-${je.id}`}>
                      Edited
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>{je.narration}</TableCell>
              <TableCell>
                <Badge variant="outline">{je.source_type}</Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">{fmtUSD(je.total_debit)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtUSD(je.total_credit)}</TableCell>
              <TableCell className="text-center">
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 hover:bg-slate-100 ${(je.attachments?.length || 0) > 0 ? "text-sky-700 font-medium" : "text-slate-400"}`}
                  onClick={(e) => { e.stopPropagation(); setSelected(je); setSelectedTab("attachments"); }}
                  data-testid={`je-attach-count-${je.id}`}
                  title={`${je.attachments?.length || 0} attachment(s)`}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  <span>{je.attachments?.length || 0}</span>
                </button>
              </TableCell>
              <TableCell><Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setSelected(je); setSelectedTab("lines"); }}>Lines</Button></TableCell>
              <TableCell className="text-right">
                {canDeleteJE ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => openDelete(je, e)}
                    data-testid={`je-delete-${je.id}`}
                    title="Delete journal entry"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
          {!items.length && (<TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">No journal entries yet.</TableCell></TableRow>)}
        </TableBody></Table></div>)}
      </CardContent></Card>
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="je-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <span>Journal Entry</span>
              {/* Bug bundle #9 (2026-08-11) — reversal / superseded / correction / edited badges */}
              {selected?.superseded_by_reversal_je_id && (
                <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-testid="badge-superseded-modal">Superseded</Badge>
              )}
              {selected?.source_type === "reversal" && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]" data-testid="badge-reversal-modal">Reversal</Badge>
              )}
              {selected?.corrects_je_id && (
                <Badge className="bg-sky-100 text-sky-800 border border-sky-300 text-[10px]" data-testid="badge-correction-modal">Correction</Badge>
              )}
              {(selected?.metadata_edit_count || 0) > 0 && (
                <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]" data-testid="badge-edited-modal">Edited</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <>
              {/* Superseded watermark banner */}
              {selected.superseded_by_reversal_je_id && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2 mb-2" data-testid="superseded-banner-modal">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="flex-1">
                    <strong>Superseded</strong> — reversed on{" "}
                    {(selected.superseded_at || "").slice(0, 10)}
                    {" by " + (selected.superseded_by_email || "?")}
                  </span>
                </div>
              )}
              {/* Bug bundle #9 (2026-08-11) — admin action row */}
              {(user?.role === "system_admin" || user?.full_access) && selected.source_type !== "reversal" && (
                <div className="flex items-center gap-2 mb-2" data-testid="je-modal-admin-actions">
                  {!selected.superseded_by_reversal_je_id && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setEditMetaOpenJE(true)}
                      data-testid="edit-metadata-btn-je"
                      className="border-slate-300 text-slate-700"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />
                      Edit metadata
                    </Button>
                  )}
                  {!selected.superseded_by_reversal_je_id && !selected.corrects_je_id && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setReverseOpenJE(true)}
                      data-testid="correct-entry-btn-je"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Correct entry
                    </Button>
                  )}
                </div>
              )}
              <NarrationEditor
                je={selected}
                canEdit={canEditNarration && selected.source_type === "manual"}
                onSaved={async (updated) => {
                  // Refresh list + swap the modal's selected JE with the
                  // freshly-updated doc so the narration reflects immediately.
                  await reload();
                  setSelected(updated);
                }}
              />
              <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2" data-testid="je-tabs">
                  <TabsTrigger value="lines" data-testid="je-tab-lines">Lines</TabsTrigger>
                  <TabsTrigger value="attachments" data-testid="je-tab-attachments">
                    <Paperclip className="h-3.5 w-3.5 mr-1.5" />
                    Attachments ({selected.attachments?.length || 0})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="lines" className="mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selected.lines.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>{l.account_code} — {l.account_name}</TableCell>
                          <TableCell className="text-right tabular-nums">{l.debit ? fmtUSD(l.debit) : ""}</TableCell>
                          <TableCell className="text-right tabular-nums">{l.credit ? fmtUSD(l.credit) : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <RecordTrail entity_type="journal_entry" entity_id={selected.id} record={selected} />
                  {/* Phase 14 (2026-06-28) — admin edit/delete shortcut for the source tx */}
                  {selected.transaction_id && (
                    <div className="flex justify-end gap-2 mt-3" data-testid="je-admin-actions">
                      <AdminTxActions
                        tx={{ id: selected.transaction_id, status: "posted",
                              reference_number: selected.reference }}
                        onChanged={() => { setSelected(null); window.location.reload(); }}
                      />
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="attachments" className="mt-4">
                  <JEAttachmentsPanel
                    je={selected}
                    canEdit={canCreateJE}
                    onChange={async () => {
                      await reload();
                      // re-fetch the currently selected JE so the panel
                      // sees the freshly updated attachments list.
                      try {
                        const { data } = await api.get(`/journal-entries/${selected.id}`);
                        setSelected(data);
                      } catch (_e) { /* noop */ }
                    }}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bug bundle #9 (2026-08-11) — Edit metadata + Reverse&Correct sub-modals for Bookkeeping */}
      <EditMetadataModal
        open={editMetaOpenJE}
        onClose={() => setEditMetaOpenJE(false)}
        je={selected}
        onSaved={async () => { setEditMetaOpenJE(false); await reload(); setSelected(null); }}
      />
      <ReverseAndCorrectModal
        open={reverseOpenJE}
        onClose={() => setReverseOpenJE(false)}
        je={selected}
        onDone={async (draft) => {
          setReverseOpenJE(false);
          setSelected(null);
          await reload();
          // Bug bundle #11 (2026-08-18) — auto-open Create Transaction
          // pre-filled with the correction_draft.
          if (draft) setCorrectionDraft(draft);
        }}
      />

      {/* Bug bundle #11 (2026-08-18) — Create Transaction modal in Correction mode */}
      <Dialog
        open={!!correctionDraft}
        onOpenChange={(o) => { if (!o) setCorrectionDraft(null); }}
      >
        <DialogContent className="max-w-3xl" data-testid="correction-create-dialog">
          <DialogHeader>
            <DialogTitle>Create Correcting JE</DialogTitle>
            <DialogDescription>
              Adjust the amounts / accounts below, then submit. The correcting entry
              will link back to the original JE once posted.
            </DialogDescription>
          </DialogHeader>
          {correctionDraft && (
            <CreateJEPanel
              correctionDraft={correctionDraft}
              onCancel={() => setCorrectionDraft(null)}
              onCreated={async () => {
                setCorrectionDraft(null);
                await reload();
                toast.success(
                  "Correcting draft submitted — verify + post it from the Approvals queue " +
                  "or by re-opening the row to close the correction chain."
                );
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* P0 (2026-07-12) — orphan-JE delete confirmation modal */}
      <Dialog open={!!delTarget} onOpenChange={(open) => { if (!open) closeDelete(); }}>
        <DialogContent className="max-w-lg" data-testid="je-delete-modal">
          <DialogHeader><DialogTitle>Delete Journal Entry</DialogTitle></DialogHeader>
          {delTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-red-100 bg-red-50 p-3 space-y-1">
                <div><span className="text-slate-500">Narration:</span> <span className="font-medium">{delTarget.narration}</span></div>
                <div className="flex justify-between gap-4 tabular-nums">
                  <span><span className="text-slate-500">Debit:</span> {fmtUSD(delTarget.total_debit)}</span>
                  <span><span className="text-slate-500">Credit:</span> {fmtUSD(delTarget.total_credit)}</span>
                </div>
                <div><span className="text-slate-500">Source:</span> <Badge variant="outline">{delTarget.source_type}</Badge></div>
                {delTarget.transaction_id && (
                  <div className="font-mono text-xs text-slate-600">tx: {delTarget.transaction_id}</div>
                )}
              </div>
              <p className="text-slate-600">
                This will permanently remove the JE and its lines. Active sources
                (posted transactions / live payroll runs) are protected by the backend.
              </p>
              {delBlock && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 space-y-2 text-sm" data-testid="je-delete-block-banner">
                  <div className="font-semibold text-red-900">
                    This JE is linked to payroll run{" "}
                    <span className="font-mono">{delBlock.payroll_run_period}</span>{" "}
                    ({delBlock.payroll_run_headcount} employees, {fmtUSD(delBlock.payroll_run_total_net)}).
                  </div>
                  <div className="text-red-800">
                    To delete this JE, first void the payroll run.
                  </div>
                  <Button size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          disabled={delBlockBusy}
                          onClick={voidRunAndRetry}
                          data-testid="je-delete-void-run-btn">
                    {delBlockBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Void payroll run &amp; retry
                  </Button>
                </div>
              )}
              {/* Phase 52-F (2026-08-07) — voided-run safe-delete banner.
                  The backend now allows purging payroll JEs whose run is
                  already voided/archived; the paired reversal JE will be
                  cascade-deleted. Show a friendly green banner instead
                  of the orange "Void payroll run & retry" button. */}
              {delTarget && !delBlock &&
                ["payroll", "payroll_void"].includes(delTarget.source_type) &&
                ["voided", "archived"].includes(delRunStatus) && (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-1 text-sm"
                     data-testid="je-delete-voided-safe-banner">
                  <div className="flex items-center gap-2 font-semibold text-emerald-900">
                    <CheckCircle2 className="h-4 w-4" />
                    Payroll run already voided — safe to delete
                  </div>
                  <div className="text-emerald-800">
                    Deleting this JE also removes the paired {delTarget.source_type === "payroll" ? "reversal" : "original"} JE
                    (they net to zero, so no ledger impact).
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="je-del-reason">Reason (required, min 5 chars)</Label>
                <Textarea
                  id="je-del-reason"
                  value={delReason}
                  onChange={(e) => setDelReason(e.target.value)}
                  placeholder="e.g. orphan historical seed — no matching transaction"
                  rows={3}
                  data-testid="je-delete-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDelete} disabled={delBusy}
                     data-testid="je-delete-cancel">Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={delBusy || delReason.trim().length < 5}
              onClick={confirmDelete}
              data-testid="je-delete-confirm"
            >
              {delBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete JE"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 52-D (2026-08-06) — Manual JE creation modal */}
      <NewJournalEntryModal
        open={showNewJE}
        onOpenChange={setShowNewJE}
        onSaved={reload}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 52-D (2026-08-06) — Manual Journal Entry creation modal.
//  Supports arbitrary N-line JEs (2..20 lines). Live Dr/Cr totals + red
//  "UNBALANCED by $X" banner until the entry balances. Sends to POST
//  /api/journal-entries which auto-generates `JE-YYYY-NNN` when reference
//  is blank. Used for the OYAK $15,552 salary onboarding case:
//     Dr 5100 15,552 / Cr 1011 13,500 / Cr 2100 2,052.
// ─────────────────────────────────────────────────────────────────────────────
function NewJournalEntryModal({ open, onOpenChange, onSaved }) {
  // Phase 5 (2026-08-09) — Reverted to pure free-form JE (Phase 1
  // baseline). The Type toggle for Payroll Run was removed since
  // payroll posting now flows through the unified "+ Create JE"
  // button on the Transactions page (Phase 53 pipeline + Verify +
  // Post). Admins get speed by verifying + posting their own draft.
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [narration, setNarration] = useState("");
  const [lines, setLines] = useState([
    { account_id: "", account_code: "", account_name: "", debit: "", credit: "" },
    { account_id: "", account_code: "", account_name: "", debit: "", credit: "" },
  ]);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  // Phase 5 (2026-08-09) — attachments picked in-modal, uploaded after
  // the JE is created (single JE-specific endpoint keeps files linked
  // to the entry rather than a floating attachment record).
  const [pending, setPending] = useState([]); // [{file, name, size}]

  useEffect(() => {
    if (!open) return;
    api.get("/accounts").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setAccounts(rows
        .filter((a) => a.is_active !== false)
        .sort((a, b) => String(a.code).localeCompare(String(b.code))));
    }).catch(() => setAccounts([]));
    // Reset form each time the modal opens
    setEntryDate(new Date().toISOString().slice(0, 10));
    setReference("");
    setNarration("");
    setLines([
      { account_id: "", account_code: "", account_name: "", debit: "", credit: "" },
      { account_id: "", account_code: "", account_name: "", debit: "", credit: "" },
    ]);
    setPending([]);
  }, [open]);

  const totalDr = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCr = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const delta = Math.round((totalDr - totalCr) * 100) / 100;
  const balanced = Math.abs(delta) < 0.005 && totalDr > 0;

  const setLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLine = () => {
    if (lines.length >= 20) return;
    setLines((prev) => [...prev, { account_id: "", account_code: "", account_name: "", debit: "", credit: "" }]);
  };
  const removeLine = (idx) => {
    if (lines.length <= 2) return;
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const pickAccount = (idx, accountId) => {
    const acc = accounts.find((a) => a.id === accountId);
    if (!acc) return;
    setLine(idx, {
      account_id: acc.id, account_code: acc.code, account_name: acc.name,
    });
  };

  const canSave = balanced
    && narration.trim().length >= 5
    && lines.every((l) => l.account_id
        && ((Number(l.debit) > 0) ^ (Number(l.credit) > 0)));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        entry_date: entryDate,
        reference: reference.trim() || undefined,
        narration: narration.trim(),
        lines: lines.map((l) => ({
          account_id: l.account_id,
          account_code: l.account_code,
          account_name: l.account_name,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        })),
      };
      const r = await api.post("/journal-entries", payload);
      const je = r.data;
      // Phase 5 (2026-08-09) — Upload any pending files to the newly
      // created JE via the Phase 52-E `/journal-entries/{id}/attachments`
      // endpoint. Failures are logged but don't roll back the JE post.
      if (pending.length && je?.id) {
        for (const p of pending) {
          try {
            const fd = new FormData();
            fd.append("file", p.file);
            await api.post(`/journal-entries/${je.id}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err) {
            console.warn("Attachment upload failed:", err);
            toast.error(`Attachment ${p.name} failed to upload.`);
          }
        }
      }
      toast.success(`Journal entry ${je.reference} posted.`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addPending = (files) => {
    setPending((prev) => [
      ...prev,
      ...Array.from(files || []).map((f) => ({ file: f, name: f.name, size: f.size })),
    ]);
  };
  const removePending = (i) => {
    setPending((prev) => prev.filter((_, idx) => idx !== i));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="new-je-modal">
        <DialogHeader>
          <DialogTitle>New Journal Entry</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Entry date</Label>
              <Input type="date" value={entryDate}
                     onChange={(e) => setEntryDate(e.target.value)}
                     data-testid="new-je-date" />
            </div>
            <div>
              <Label className="text-xs">Reference (optional)</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)}
                     placeholder="Auto-generated JE-YYYY-NNNN if blank"
                     data-testid="new-je-reference" />
            </div>
            <div className="col-span-1 text-right">
              <Label className="text-xs">Status</Label>
              <div className="pt-2">
                {balanced ? (
                  <Badge className="bg-emerald-100 text-emerald-700"
                         data-testid="new-je-balanced-badge">Balanced ✓</Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-700"
                         data-testid="new-je-unbalanced-badge">
                    Unbalanced by ${Math.abs(delta).toFixed(2)}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Narration (required, min 5 chars)</Label>
            <Textarea value={narration} onChange={(e) => setNarration(e.target.value)}
                      placeholder="e.g. May 2026 salary payment to 3 employees — Dr 5100 / Cr 1011 net / Cr 2100 PAYE"
                      rows={2} data-testid="new-je-narration" />
          </div>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50%]">Account</TableHead>
                  <TableHead className="text-right w-[18%]">Debit</TableHead>
                  <TableHead className="text-right w-[18%]">Credit</TableHead>
                  <TableHead className="w-[14%] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l, idx) => (
                  <TableRow key={idx} data-testid={`new-je-line-${idx}`}>
                    <TableCell>
                      <Select value={l.account_id || ""}
                              onValueChange={(v) => pickAccount(idx, v)}>
                        <SelectTrigger data-testid={`new-je-line-${idx}-account`}>
                          <SelectValue placeholder="Pick an account…" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {accounts.map((a) => (
                            <SelectItem key={a.id} value={a.id}
                                        data-testid={`new-je-acc-${a.code}`}>
                              {a.code} — {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" min="0" value={l.debit}
                             onChange={(e) => setLine(idx, { debit: e.target.value, credit: e.target.value ? "" : l.credit })}
                             className="text-right tabular-nums"
                             data-testid={`new-je-line-${idx}-debit`} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" min="0" value={l.credit}
                             onChange={(e) => setLine(idx, { credit: e.target.value, debit: e.target.value ? "" : l.debit })}
                             className="text-right tabular-nums"
                             data-testid={`new-je-line-${idx}-credit`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon"
                              disabled={lines.length <= 2}
                              onClick={() => removeLine(idx)}
                              data-testid={`new-je-line-${idx}-remove`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between p-2 border-t bg-slate-50">
              <Button variant="outline" size="sm"
                      disabled={lines.length >= 20}
                      onClick={addLine}
                      data-testid="new-je-add-line">
                <Plus className="h-3 w-3 mr-1" /> Add line
              </Button>
              <div className="flex gap-6 text-sm tabular-nums pr-3">
                <div>Total Dr: <span className="font-semibold" data-testid="new-je-total-dr">${totalDr.toFixed(2)}</span></div>
                <div>Total Cr: <span className="font-semibold" data-testid="new-je-total-cr">${totalCr.toFixed(2)}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Phase 5 (2026-08-09) — In-modal attachment picker. Files are
            uploaded to the JE via /journal-entries/{id}/attachments
            after the entry is created. */}
        <div className="space-y-2 text-sm" data-testid="new-je-attachments">
          <Label className="text-xs">Attachments (optional)</Label>
          <label
            htmlFor="new-je-file-input"
            className="flex items-center justify-center gap-2 border border-dashed rounded-md p-3 text-xs text-slate-500 cursor-pointer hover:bg-slate-50 transition-colors"
            onDrop={(e) => { e.preventDefault(); addPending(e.dataTransfer.files); }}
            onDragOver={(e) => e.preventDefault()}
            data-testid="new-je-file-drop"
          >
            <Paperclip className="h-4 w-4" />
            <span>Drag &amp; drop, or click to browse (PDF, JPG, PNG, XLSX, DOCX)</span>
            <input
              id="new-je-file-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.docx,.doc"
              className="hidden"
              onChange={(e) => addPending(e.target.files)}
              data-testid="new-je-file-input"
            />
          </label>
          {pending.length > 0 && (
            <ul className="space-y-1">
              {pending.map((p, i) => (
                <li key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1"
                    data-testid={`new-je-att-item-${i}`}>
                  <span className="truncate">{p.name} <span className="text-slate-400">({Math.round(p.size / 1024)} KB)</span></span>
                  <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => removePending(i)}
                          data-testid={`new-je-att-remove-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  disabled={busy} data-testid="new-je-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!canSave || busy}
                  className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                  data-testid="new-je-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Post Journal Entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 2 (2026-08-09) — Payroll Run panel (shared by Path A & Path B).
//
//  Renders a snapshot-driven payroll form:
//    • Period year/month + payment date + bank account picker.
//    • Auto-loads snapshot from POST /api/payroll/snapshot.
//    • Table with side-by-side Snapshot vs Actual columns per employee.
//    • Variance rows highlighted amber; require a Variance Note before
//      the panel can Submit.
//    • Live proposed JE preview (Dr 5100 gross / Dr 6510 PAYE / Cr bank /
//      Cr 2100 PAYE) sourced from the backend so what you see is exactly
//      what will post.
//    • Submit action is customisable via `submitLabel` + `onSubmit`:
//      Path A submits via POST /api/transactions/payroll-draft (goes into
//      the pipeline). Path B submits via POST /api/journal-entries/
//      payroll-direct (immediate JE, source_type="payroll_direct").
// ─────────────────────────────────────────────────────────────────────────────
export function PayrollRunPanel({
  onPosted,
  onCancel,
  mode = "direct",           // "direct" (Path B admin) | "pipeline" (Path A staff)
  submitLabel,
  successToastPrefix = "Payroll JE",
}) {
  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [year, setYear] = useState(nextMonth.getFullYear());
  const [month, setMonth] = useState(nextMonth.getMonth() + 1);
  const [paymentDate, setPaymentDate] = useState(() => nextMonth.toISOString().slice(0, 10));
  const [bankCode, setBankCode] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [proposedJe, setProposedJe] = useState(null);
  const [overrides, setOverrides] = useState({});     // { employee_id: {base_salary?, paye_tax?, other_deductions?, variance_note?} }
  const [note, setNote] = useState("");
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load bank/cash accounts (Phase 51-B whitelist mirror).
  const isBankOrCashAccount = (a) => {
    if (!a || a.type !== "asset" || a.is_active === false) return false;
    const code = String(a.code || "").trim();
    if (!code) return false;
    if (code === "1000" || code === "1100") return true;
    if (a.parent_code === "1010") return true;
    const n = Number(code);
    return Number.isInteger(n) && n >= 1010 && n <= 1099;
  };
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/accounts");
        const banks = (data || [])
          .filter(isBankOrCashAccount)
          .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
        setBankAccounts(banks);
      } catch { /* empty */ }
    })();
  }, []);

  // Fetch snapshot whenever year/month/bankCode/overrides change.
  const fetchSnapshot = async () => {
    if (!year || !month) return;
    setLoadingSnap(true);
    try {
      const payloadOverrides = Object.entries(overrides).map(([eid, o]) => ({
        employee_id: eid,
        ...(o.base_salary !== undefined && o.base_salary !== "" ? { base_salary: Number(o.base_salary) } : {}),
        ...(o.paye_tax !== undefined && o.paye_tax !== "" ? { paye_tax: Number(o.paye_tax) } : {}),
        ...(o.other_deductions !== undefined && o.other_deductions !== "" ? { other_deductions: Number(o.other_deductions) } : {}),
        ...(o.variance_note ? { variance_note: o.variance_note } : {}),
      }));
      const { data } = await api.post("/payroll/snapshot", {
        period_year: Number(year),
        period_month: Number(month),
        payment_date: paymentDate || undefined,
        bank_account_code: bankCode || undefined,
        overrides: payloadOverrides.length ? payloadOverrides : undefined,
      });
      setSnapshot(data.snapshot);
      setProposedJe(data.proposed_je);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingSnap(false);
    }
  };
  // Refresh snapshot on period/bank change. Overrides are refreshed only
  // when the user commits an edit (blur) so we don't fire on every
  // keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSnapshot(); }, [year, month, paymentDate, bankCode]);

  const setOverride = (eid, patch) => {
    setOverrides((prev) => ({ ...prev, [eid]: { ...(prev[eid] || {}), ...patch } }));
  };

  const varianceRowsMissingNote = (snapshot?.lines || []).filter(
    (l) => l.has_variance && !(overrides[l.employee_id]?.variance_note || l.variance_note),
  );
  const canSubmit = snapshot
    && Number(snapshot?.totals?.gross || 0) > 0
    && bankCode
    && paymentDate
    && proposedJe?.balanced
    && varianceRowsMissingNote.length === 0
    && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payloadOverrides = Object.entries(overrides).map(([eid, o]) => ({
        employee_id: eid,
        ...(o.base_salary !== undefined && o.base_salary !== "" ? { base_salary: Number(o.base_salary) } : {}),
        ...(o.paye_tax !== undefined && o.paye_tax !== "" ? { paye_tax: Number(o.paye_tax) } : {}),
        ...(o.other_deductions !== undefined && o.other_deductions !== "" ? { other_deductions: Number(o.other_deductions) } : {}),
        ...(o.variance_note ? { variance_note: o.variance_note } : {}),
      }));
      const url = mode === "pipeline" ? "/transactions/payroll-draft" : "/journal-entries/payroll-direct";
      const body = {
        period_year: Number(year),
        period_month: Number(month),
        payment_date: paymentDate,
        bank_account_code: bankCode,
        overrides: payloadOverrides.length ? payloadOverrides : undefined,
        ...(mode === "pipeline" ? { note: note || undefined } : {}),
      };
      const { data } = await api.post(url, body);
      onPosted?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const paidLabel = mode === "pipeline" ? "Submit for verification" : "Post Payroll JE";
  return (
    <div className="space-y-4 text-sm" data-testid="payroll-run-panel">
      <div className="grid grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Year</Label>
          <Input type="number" value={year} onChange={(e) => setYear(e.target.value)}
                 data-testid="pr-year" />
        </div>
        <div>
          <Label className="text-xs">Month</Label>
          <Input type="number" min="1" max="12" value={month}
                 onChange={(e) => setMonth(e.target.value)}
                 data-testid="pr-month" />
        </div>
        <div>
          <Label className="text-xs">Payment date</Label>
          <Input type="date" value={paymentDate}
                 onChange={(e) => setPaymentDate(e.target.value)}
                 data-testid="pr-payment-date" />
        </div>
        <div>
          <Label className="text-xs">Pay-from account</Label>
          <Select value={bankCode} onValueChange={setBankCode}>
            <SelectTrigger data-testid="pr-bank-select">
              <SelectValue placeholder="Select a bank or cash account" />
            </SelectTrigger>
            <SelectContent>
              {bankAccounts.map((a) => (
                <SelectItem key={a.id} value={a.code} data-testid={`pr-bank-option-${a.code}`}>
                  {a.code} — {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {mode === "pipeline" && (
        <div>
          <Label className="text-xs">Note (optional)</Label>
          <Textarea rows={2} value={note}
                    placeholder="e.g., September 2026 payroll — 3 employees on unpaid leave"
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="pr-note" />
        </div>
      )}

      {/* Employee snapshot / actual grid */}
      <div className="border rounded-md overflow-x-auto">
        <Table data-testid="pr-employee-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[22%]">Employee</TableHead>
              <TableHead className="text-right w-[10%]">Snapshot Gross</TableHead>
              <TableHead className="text-right w-[12%]">Actual Gross</TableHead>
              <TableHead className="text-right w-[10%]">Snap PAYE</TableHead>
              <TableHead className="text-right w-[12%]">Actual PAYE</TableHead>
              <TableHead className="text-right w-[10%]">Other Ded.</TableHead>
              <TableHead className="w-[24%]">Variance Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingSnap && (
              <TableRow><TableCell colSpan={7} className="text-center py-4">
                <Loader2 className="inline animate-spin h-4 w-4" /> Loading snapshot…
              </TableCell></TableRow>
            )}
            {!loadingSnap && !(snapshot?.lines?.length) && (
              <TableRow><TableCell colSpan={7} className="text-center py-4 text-slate-500">
                No active employees for this period.
              </TableCell></TableRow>
            )}
            {(snapshot?.lines || []).map((l) => {
              const ov = overrides[l.employee_id] || {};
              const rowMissingNote = l.has_variance && !(ov.variance_note || l.variance_note);
              return (
                <TableRow key={l.employee_id}
                          className={l.has_variance ? "bg-amber-50" : ""}
                          data-testid={`pr-row-${l.employee_id}`}>
                  <TableCell className="font-medium">
                    {l.employee_name}
                    {l.has_variance && (
                      <Badge className="ml-1 bg-amber-200 text-amber-900" data-testid={`pr-variance-badge-${l.employee_id}`}>Variance</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">
                    {fmtUSD(l.snapshot.gross)}
                  </TableCell>
                  <TableCell>
                    <Input type="number" step="0.01" min="0"
                           value={ov.base_salary ?? l.actual.gross}
                           onChange={(e) => setOverride(l.employee_id, { base_salary: e.target.value })}
                           onBlur={fetchSnapshot}
                           className="text-right tabular-nums h-8"
                           data-testid={`pr-actual-gross-${l.employee_id}`} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">
                    {fmtUSD(l.snapshot.paye)}
                  </TableCell>
                  <TableCell>
                    <Input type="number" step="0.01" min="0"
                           value={ov.paye_tax ?? l.actual.paye}
                           onChange={(e) => setOverride(l.employee_id, { paye_tax: e.target.value })}
                           onBlur={fetchSnapshot}
                           className="text-right tabular-nums h-8"
                           data-testid={`pr-actual-paye-${l.employee_id}`} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step="0.01" min="0"
                           value={ov.other_deductions ?? l.actual.other_deductions}
                           onChange={(e) => setOverride(l.employee_id, { other_deductions: e.target.value })}
                           onBlur={fetchSnapshot}
                           className="text-right tabular-nums h-8"
                           data-testid={`pr-actual-other-${l.employee_id}`} />
                  </TableCell>
                  <TableCell>
                    {l.has_variance ? (
                      <Input value={ov.variance_note ?? ""}
                             onChange={(e) => setOverride(l.employee_id, { variance_note: e.target.value })}
                             placeholder="Required — reason for delta"
                             className={`h-8 ${rowMissingNote ? "border-red-400" : ""}`}
                             data-testid={`pr-variance-note-${l.employee_id}`} />
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {snapshot?.totals && (
              <TableRow className="bg-slate-100 font-semibold" data-testid="pr-totals">
                <TableCell>Totals</TableCell>
                <TableCell className="text-right tabular-nums text-slate-600">—</TableCell>
                <TableCell className="text-right tabular-nums">{fmtUSD(snapshot.totals.gross)}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-600">—</TableCell>
                <TableCell className="text-right tabular-nums">{fmtUSD(snapshot.totals.paye)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtUSD(snapshot.totals.other_deductions)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Proposed JE preview */}
      {proposedJe?.lines && (
        <div className="rounded-md border bg-slate-50 p-3" data-testid="pr-proposed-je">
          <p className="text-xs font-medium text-slate-600 mb-2">
            Proposed Journal Entry (dated {paymentDate})
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposedJe.lines.map((l, i) => (
                <TableRow key={i} data-testid={`pr-je-line-${l.account_code}`}>
                  <TableCell className="font-medium">{l.account_code} {l.account_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{l.debit > 0 ? fmtUSD(l.debit) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{l.credit > 0 ? fmtUSD(l.credit) : "—"}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-100 font-semibold">
                <TableCell>Totals</TableCell>
                <TableCell className="text-right tabular-nums" data-testid="pr-je-total-dr">{fmtUSD(proposedJe.total_debit)}</TableCell>
                <TableCell className="text-right tabular-nums" data-testid="pr-je-total-cr">{fmtUSD(proposedJe.total_credit)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {!proposedJe.balanced && (
            <p className="text-xs text-red-600 mt-2">
              Proposed JE not balanced — check the snapshot totals.
            </p>
          )}
        </div>
      )}
      {proposedJe?.error && (
        <p className="text-xs text-red-600" data-testid="pr-proposed-je-error">
          {proposedJe.error}
        </p>
      )}

      {varianceRowsMissingNote.length > 0 && (
        <p className="text-xs text-red-600" data-testid="pr-variance-notes-missing">
          {varianceRowsMissingNote.length} variance row(s) require a note before submit.
        </p>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="pr-cancel">Cancel</Button>
        <Button onClick={submit}
                disabled={!canSubmit}
                className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                data-testid="pr-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {submitLabel || paidLabel}
        </Button>
      </DialogFooter>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 52-F (2026-08-07) — Editable narration header on the JE detail modal.
//  Renders the narration text with a pencil icon for manual JEs (when the
//  user holds `accounting.bookkeeping.edit_je_narration`). Clicking the
//  pencil switches to an inline textarea + required "reason" input. Save
//  calls PATCH /journal-entries/{id} with {narration, reason}; backend
//  rejects any additional field with 400 immutable_fields.
// ─────────────────────────────────────────────────────────────────────────────

function NarrationEditor({ je, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(je.narration || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Keep the local text state in sync when a fresh JE is selected.
  useEffect(() => {
    setText(je.narration || "");
    setReason("");
    setEditing(false);
  }, [je.id, je.narration]);

  const doSave = async () => {
    const t = text.trim();
    const r = reason.trim();
    if (t === (je.narration || "").trim()) {
      toast.error("Narration is unchanged.");
      return;
    }
    if (t.length < 5) {
      toast.error("Narration must be at least 5 characters.");
      return;
    }
    if (r.length < 5) {
      toast.error("Please enter a reason (min 5 characters).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/journal-entries/${je.id}`, {
        narration: t,
        reason: r,
      });
      toast.success("Narration updated.");
      setEditing(false);
      setReason("");
      if (onSaved) await onSaved(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="mb-3 flex items-start gap-2 text-sm text-slate-600" data-testid="je-narration-view">
        <span className="flex-1">{je.narration}</span>
        {canEdit && (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-slate-500 hover:text-[#4FA3D1] hover:bg-sky-50"
            onClick={() => setEditing(true)}
            title="Edit narration"
            data-testid="je-narration-edit-btn"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="mb-3 space-y-2 rounded-md border border-sky-200 bg-sky-50/50 p-3" data-testid="je-narration-editor">
      <div>
        <Label className="text-xs text-slate-500" htmlFor="je-narration-text">
          Narration
        </Label>
        <Textarea
          id="je-narration-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          className="text-sm"
          data-testid="je-narration-text"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500" htmlFor="je-narration-reason">
          Reason for change (required, min 5 chars) — recorded in audit log
        </Label>
        <Input
          id="je-narration-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Corrected month attribution — work performed in April, paid in arrears"
          data-testid="je-narration-reason"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => { setEditing(false); setText(je.narration || ""); setReason(""); }}
          data-testid="je-narration-cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
          disabled={busy || text.trim().length < 5 || reason.trim().length < 5}
          onClick={doSave}
          data-testid="je-narration-save"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
          Save
        </Button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
//  Phase 52-F (2026-08-07) — JE Attachments panel.
//  Rendered inside the JE detail modal (Attachments tab) and lets users
//  upload / list / download / delete supporting documents attached to a
//  journal entry. Files up to 25MB each, 10 per JE, whitelist mimes.
// ─────────────────────────────────────────────────────────────────────────────

const JE_ATT_ALLOWED_EXT = ".pdf,.png,.jpg,.jpeg,.webp,.csv,.xls,.xlsx,.doc,.docx";
const JE_ATT_MAX_COUNT = 10;
const JE_ATT_MAX_MB = 25;

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function JEAttachmentsPanel({ je, canEdit, onChange }) {
  const items = je.attachments || [];
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);

  const doUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    if (items.length + fileList.length > JE_ATT_MAX_COUNT) {
      toast.error(`This JE can hold at most ${JE_ATT_MAX_COUNT} attachments.`);
      return;
    }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const f of Array.from(fileList)) {
      if (f.size > JE_ATT_MAX_MB * 1024 * 1024) {
        toast.error(`${f.name}: exceeds ${JE_ATT_MAX_MB} MB limit.`);
        fail += 1;
        continue;
      }
      const form = new FormData();
      form.append("file", f);
      try {
        await api.post(`/journal-entries/${je.id}/attachments`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error((d && (d.message || d)) || formatApiError(e));
        fail += 1;
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} attached.`);
    await onChange();
  };

  const doDelete = async (attId) => {
    setDelId(attId);
    try {
      await api.delete(`/journal-entries/${je.id}/attachments/${attId}`);
      toast.success("Attachment removed.");
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDelId(null);
    }
  };

  const doDownload = async (a) => {
    try {
      const { data } = await api.post(`/attachments/${a.id}/download-token`);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      // Open in a new tab so PDF preview works and CSVs auto-download.
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="je-attachments-panel">
      {canEdit && (
        <div className="flex items-center gap-3">
          <label
            htmlFor={`je-att-upload-${je.id}`}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm cursor-pointer ${busy || items.length >= JE_ATT_MAX_COUNT ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-[#4FA3D1] text-white hover:bg-[#2F86B5]"}`}
            data-testid="je-att-upload-label"
          >
            <Upload className="h-4 w-4" />
            {busy ? "Uploading..." : "Upload files"}
          </label>
          <input
            id={`je-att-upload-${je.id}`}
            type="file"
            multiple
            className="hidden"
            accept={JE_ATT_ALLOWED_EXT}
            disabled={busy || items.length >= JE_ATT_MAX_COUNT}
            onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }}
            data-testid="je-att-upload-input"
          />
          <span className="text-xs text-slate-500">
            {items.length}/{JE_ATT_MAX_COUNT} files · up to {JE_ATT_MAX_MB} MB each · PDF, PNG, JPG, WEBP, CSV, XLS, XLSX, DOC, DOCX
          </span>
        </div>
      )}
      {items.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm border border-dashed rounded-md" data-testid="je-att-empty">
          <Paperclip className="h-6 w-6 mx-auto mb-2 text-slate-300" />
          No attachments yet.
          {canEdit && <div className="text-xs mt-1">Upload receipts, contracts, or bank statements to keep them with this entry.</div>}
        </div>
      ) : (
        <div className="border rounded-md divide-y">
          {items.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                 data-testid={`je-att-row-${a.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="font-medium truncate" title={a.filename}>{a.filename}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {fmtBytes(a.size_bytes)} · {a.mime_type} · {fmtDate(a.uploaded_at)} · {a.uploaded_by_email || a.uploaded_by}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm" variant="outline"
                  onClick={() => doDownload(a)}
                  data-testid={`je-att-download-${a.id}`}
                >
                  <Download className="h-3.5 w-3.5 mr-1" /> Download
                </Button>
                {canEdit && (
                  <Button
                    size="icon" variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                    disabled={delId === a.id}
                    onClick={() => doDelete(a.id)}
                    data-testid={`je-att-delete-${a.id}`}
                    title="Remove attachment"
                  >
                    {delId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
//  AP / AR (parameterised — `kind` = 'ap' or 'ar')
// ─────────────────────────────────────────────────────────────────────────────
function AgingTable({ aging }) {
  const colors = { "0-30": "bg-emerald-100 text-emerald-700", "31-60": "bg-amber-100 text-amber-700",
                    "61-90": "bg-orange-100 text-orange-700", "90+": "bg-red-100 text-red-700" };
  return (
    <Table data-testid="aging-table"><TableHeader><TableRow>
      <TableHead>Bucket</TableHead><TableHead className="text-right">Total</TableHead>
    </TableRow></TableHeader><TableBody>
      {(aging.buckets || []).map((b) => (
        <TableRow key={b.bucket}><TableCell><Badge variant="outline" className={colors[b.bucket]}>{b.bucket} days</Badge></TableCell>
          <TableCell className="text-right tabular-nums font-semibold">{fmtUSD(b.total)}</TableCell></TableRow>))}
      <TableRow><TableCell className="font-bold">Grand Total</TableCell>
        <TableCell className="text-right tabular-nums font-bold">{fmtUSD(aging.grand_total)}</TableCell></TableRow>
    </TableBody></Table>
  );
}

export function ApArPage({ kind = "ap" }) {
  const { user } = useAuth();
  const canPay = [ROLES.SYSTEM_ADMIN, ROLES.SALIHI_ACCOUNTANT, ROLES.SALIHI_SENIOR_ACCOUNTANT, ROLES.SALIHI_FINANCE_MANAGER].includes(user?.role)
                 || user?.full_access === true;
  const [invoices, setInvoices] = useState([]); const [aging, setAging] = useState({ buckets: [], grand_total: 0 });
  const [loading, setLoading] = useState(true); const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null); const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("bank_transfer"); const [payRef, setPayRef] = useState(""); const [payDate, setPayDate] = useState(new Date().toISOString().slice(0,10));

  const load = async () => {
    setLoading(true);
    try {
      const [inv, ag] = await Promise.all([
        api.get(`/${kind}/invoices`), api.get(`/${kind}/aging`),
      ]);
      setInvoices(inv.data); setAging(ag.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const recordPayment = async () => {
    if (!payTarget || !payAmount) return;
    try {
      const path = kind === "ap" ? "/ap/payments" : "/ar/receipts";
      await api.post(path, {
        payment_date: payDate, kind, amount: Number(payAmount),
        payment_method: payMethod, reference: payRef,
        applied_to: [{ invoice_transaction_id: payTarget.id, amount: Number(payAmount) }],
      });
      toast.success(kind === "ap" ? "Payment recorded." : "Receipt recorded.");
      setPayOpen(false); setPayTarget(null); setPayAmount(""); setPayRef("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const label = kind === "ap" ? "Accounts Payable" : "Accounts Receivable";

  return (
    <div className="space-y-5" data-testid={`${kind}-page`}>
      <BackToDashBar />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="text-2xl font-bold">{label}</h2>
          <p className="text-sm text-slate-500 mt-1">{kind === "ap" ? "Outstanding supplier invoices and aging." : "Outstanding customer invoices and aging."}</p></div>
      </div>
      <Tabs defaultValue="invoices">
        <TabsList><TabsTrigger value="invoices" data-testid="tab-invoices">Open Invoices</TabsTrigger><TabsTrigger value="aging" data-testid="tab-aging">Aging</TabsTrigger></TabsList>
        <TabsContent value="invoices">
          <Card><CardContent className="p-0">
            {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
            : (<div className="overflow-x-auto"><Table data-testid="invoices-table"><TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>{kind === "ap" ? "Supplier" : "Customer"}</TableHead>
              <TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Outstanding</TableHead>
              <TableHead>Age</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader><TableBody>
              {invoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  data-testid={`inv-${inv.id}`}
                  onClick={() => { window.location.href = `/transactions/${inv.id}`; }}
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  <TableCell>{fmtDate(inv.transaction_date)}</TableCell>
                  <TableCell>{inv.counterparty_name || "—"}</TableCell>
                  <TableCell>{inv.reference_number || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtUSD(inv.amount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtUSD(inv.paid)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmtUSD(inv.outstanding)}</TableCell>
                  <TableCell><Badge variant="outline">{inv.age_days}d / {inv.bucket}</Badge></TableCell>
                  <TableCell className="text-right">
                    {canPay && (<Button variant="outline" size="sm"
                      onClick={(e) => { e.stopPropagation(); setPayTarget(inv); setPayAmount(String(inv.outstanding)); setPayOpen(true); }}
                      data-testid={`record-pay-${inv.id}`}>
                      {kind === "ap" ? "Pay" : "Receive"}
                    </Button>)}
                  </TableCell>
                </TableRow>))}
              {!invoices.length && (<TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No open invoices.</TableCell></TableRow>)}
            </TableBody></Table></div>)}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="aging"><Card><CardContent>{loading ? <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /> : <AgingTable aging={aging} />}</CardContent></Card></TabsContent>
      </Tabs>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>{kind === "ap" ? "Record Payment" : "Record Receipt"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">{payTarget?.counterparty_name} · {payTarget?.reference_number} · Outstanding {fmtUSD(payTarget?.outstanding)}</div>
            <div><Label>Date</Label><Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="pay-date" /></div>
            <div><Label>Amount (USD)</Label><Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="pay-amount" /></div>
            <div><Label>Method</Label>
              <Select value={payMethod} onValueChange={setPayMethod}><SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent></Select>
            </div>
            <div><Label>Reference</Label><Input value={payRef} onChange={(e) => setPayRef(e.target.value)} data-testid="pay-ref" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={recordPayment} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="pay-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  CASHBOOK
// ─────────────────────────────────────────────────────────────────────────────
// Bug bundle #16 (2026-08-19) — Cashbook rebuilt on the new dynamic
// endpoints (/api/bank/cash-accounts + /api/bank/ledger). Tiles come from
// the CoA (not the four hardcoded seed codes), and clicking a tile filters
// the ledger to that account with a proper running balance. Every JE line
// touching the account is included regardless of source_type — payroll,
// expense, loan, transfer, refund all show up.
export function CashbookPage() {
  const [accounts, setAccounts] = useState([]);
  const [ledger, setLedger] = useState({ rows: [], opening: [], closing: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadAccounts = async () => {
    try {
      const { data } = await api.get("/bank/cash-accounts");
      const accs = (data?.accounts || [])
        .filter((a) => Math.abs(a.balance || 0) > 0.001)  // hide empty test-pollution accounts
        .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
      setAccounts(accs);
      // Pick the highest-absolute-balance account as the default focus.
      if (!selectedCode && accs.length) {
        const top = accs.slice().sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))[0];
        setSelectedCode(top?.code || accs[0].code);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async () => {
    if (!selectedCode) return;
    setLedgerLoading(true);
    try {
      const params = { account_code: selectedCode, limit: 500 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/bank/ledger", { params });
      setLedger(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => { loadAccounts(); }, []);   // eslint-disable-line
  useEffect(() => { loadLedger(); }, [selectedCode, dateFrom, dateTo]);   // eslint-disable-line

  const selectedAcc = accounts.find((a) => a.code === selectedCode);

  const SOURCE_STYLE = {
    payroll:    "bg-purple-100 text-purple-800 border-purple-200",
    expense:    "bg-rose-100 text-rose-800 border-rose-200",
    income:     "bg-emerald-100 text-emerald-800 border-emerald-200",
    loan:       "bg-amber-100 text-amber-800 border-amber-200",
    transfer:   "bg-sky-100 text-sky-800 border-sky-200",
    refund:     "bg-blue-100 text-blue-800 border-blue-200",
    bank_fee:   "bg-slate-100 text-slate-800 border-slate-200",
    adjustment: "bg-orange-100 text-orange-800 border-orange-200",
    manual:     "bg-slate-100 text-slate-700 border-slate-200",
  };

  return (
    <div className="space-y-5" data-testid="cashbook-page">
      <BackToDashBar />
      <h2 className="text-2xl font-bold">Cashbook</h2>
      <p className="text-sm text-slate-500 -mt-3">
        Every cash / bank account discovered from the Chart of Accounts, with the full ledger of postings touching it.
      </p>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" />
        </div>
      ) : accounts.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">
          No cash or bank accounts with movements yet.
        </CardContent></Card>
      ) : (
        <>
          {/* Tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {accounts.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setSelectedCode(a.code)}
                data-testid={`cashbook-tile-${a.code}`}
                className={`text-left transition-shadow ${
                  a.code === selectedCode
                    ? "ring-2 ring-[#4FA3D1] shadow-md"
                    : "hover:shadow-sm"
                }`}
              >
                <Card>
                  <CardContent className="p-4">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                      <Banknote className="h-3 w-3" /> {a.code} · {a.name}
                    </p>
                    <p className={`text-2xl font-bold tabular-nums mt-1 ${a.balance < 0 ? "text-rose-600" : ""}`}>
                      {fmtUSD(a.balance)}
                    </p>
                    {a.is_bank_subaccount && (
                      <Badge variant="outline" className="mt-1 text-[10px]">Bank sub</Badge>
                    )}
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>

          {/* Date filters */}
          <Card>
            <CardContent className="p-3 flex flex-wrap gap-3 items-end">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                       data-testid="cashbook-date-from" className="h-9" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                       data-testid="cashbook-date-to" className="h-9" />
              </div>
              <div className="ml-auto text-xs text-slate-500">
                {selectedAcc ? (
                  <>Showing <strong>{selectedAcc.code} {selectedAcc.name}</strong> · closing {fmtUSD(ledger.closing?.[0]?.balance ?? selectedAcc.balance)}</>
                ) : "Pick a tile above to view its ledger"}
              </div>
            </CardContent>
          </Card>

          {/* Ledger */}
          <Card><CardContent className="p-0">
            {ledgerLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="cashbook-ledger">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>JE Ref</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Narration</TableHead>
                      <TableHead>Counterparty</TableHead>
                      <TableHead className="text-right">Debit (In)</TableHead>
                      <TableHead className="text-right">Credit (Out)</TableHead>
                      <TableHead className="text-right">Running Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No movements for this account in the selected window.
                        </TableCell>
                      </TableRow>
                    )}
                    {ledger.rows.map((r, i) => (
                      <TableRow key={`${r.je_id}-${i}`} data-testid={`cashbook-row-${i}`}>
                        <TableCell className="whitespace-nowrap">{fmtDate(r.entry_date)}</TableCell>
                        <TableCell className="font-mono text-xs">{r.je_reference || r.je_id?.slice(0, 8) || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${SOURCE_STYLE[r.source_type] || SOURCE_STYLE.manual}`}>
                            {r.source_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate">{r.narration}</TableCell>
                        <TableCell className="max-w-[180px] truncate text-slate-600">{r.counterparty_name || "—"}</TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-700">
                          {r.debit > 0 ? fmtUSD(r.debit) : ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-rose-700">
                          {r.credit > 0 ? fmtUSD(r.credit) : ""}
                        </TableCell>
                        <TableCell className={`text-right tabular-nums font-semibold ${r.running_balance < 0 ? "text-rose-600" : ""}`}>
                          {fmtUSD(r.running_balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PAYROLL
// ─────────────────────────────────────────────────────────────────────────────
export function PayrollPage() {
  // Phase 2 (2026-08-09) — Payroll Accounting is now VIEW-ONLY for all
  // roles. New payroll runs are created either by staff via the
  // Transaction Submission page ("New Payroll Run" modal, Phase 53
  // pipeline) or by admins via Bookkeeping → Adjustment JE (Payroll
  // Run toggle, `source_type=payroll_direct`). This page kept only for
  // historical browsing + audit.
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll-runs");
      setRuns(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const STATUS_COL = {
    draft:    "bg-slate-100 text-slate-700",
    approved: "bg-amber-100 text-amber-700",
    posted:   "bg-blue-100 text-blue-700",
    paid:     "bg-emerald-100 text-emerald-700",
    voided:   "bg-red-100 text-red-700",
  };
  const visibleRuns = runs.filter((r) => showArchived || !r.archived);

  return (
    <div className="space-y-5" data-testid="payroll-page">
      <BackToDashBar />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Payroll Accounting</h2>
          <p className="text-sm text-slate-500 mt-1">Historical payroll runs — read-only.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="payroll-show-archived-label">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="payroll-show-archived"
            />
            Show archived
          </label>
        </div>
      </div>

      {/* Read-only info banner */}
      <div
        className="rounded-lg border border-sky-200 bg-sky-50 p-4 flex items-start gap-3"
        data-testid="payroll-readonly-banner"
      >
        <AlertTriangle className="h-5 w-5 text-sky-700 shrink-0 mt-0.5" />
        <div className="text-sm text-sky-900">
          <p className="font-medium">Payroll Accounting is read-only.</p>
          <p className="mt-1 text-sky-800">
            New payroll runs are now posted from the{" "}
            <Link to="/transactions" className="underline font-medium">Transaction Submission</Link>{" "}
            page — click <span className="font-medium">+ Create JE</span> and pick <span className="font-medium">Payroll</span> as the type.
            The run flows through Verify → Post like any other transaction; admins can verify and post their own drafts for speed.
            This page keeps the historical runs so voided/archived entries stay auditable.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="payroll-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead className="hidden md:table-cell">Owner</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRuns.map((r) => (
                    <TableRow
                      key={r.id}
                      data-testid={`payroll-row-${r.id}`}
                      onClick={async () => {
                        const { data } = await api.get(`/payroll-runs/${r.id}`);
                        setDetail(data);
                      }}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <TableCell>{r.period_year}-{String(r.period_month).padStart(2, "0")}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COL[r.status] || "bg-slate-100 text-slate-700"}>{r.status}</Badge>
                        {r.status === "posted" && r.accrued_only && (
                          <Badge className="ml-1 bg-amber-100 text-amber-800" data-testid={`payroll-accrued-badge-${r.id}`}>Accrued (unpaid)</Badge>
                        )}
                        {r.status === "paid" && r.paid_date && (
                          <Badge className="ml-1 bg-emerald-100 text-emerald-800" data-testid={`payroll-paid-badge-${r.id}`}>Paid {r.paid_date}</Badge>
                        )}
                        {r.archived && (
                          <Badge className="ml-1 bg-slate-200 text-slate-500" data-testid={`payroll-archived-${r.id}`}>archived</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUSD(r.total_gross)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUSD(r.total_deductions)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{fmtUSD(r.total_net)}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <OwnerCell
                          created_by_email={r.created_by_email}
                          created_at={r.created_at}
                          updated_by_email={r.posted_by_email || r.approved_by_email}
                          updated_at={r.updated_at}
                        />
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const { data } = await api.get(`/payroll-runs/${r.id}`);
                            setDetail(data);
                          }}
                          data-testid={`payroll-detail-${r.id}`}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!visibleRuns.length && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No payroll runs yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payroll {detail?.period_year}-{String(detail?.period_month).padStart(2, "0")}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="overflow-x-auto">
              <Table data-testid="payroll-detail-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead className="text-right">Net Salary</TableHead>
                    <TableHead className="text-right">PAYE</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.lines.map((l) => (
                    <TableRow key={l.employee_id}>
                      <TableCell>{l.employee_name}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUSD(l.base_salary)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{fmtUSD(l.gross)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUSD(l.paye_tax)}</TableCell>
                    </TableRow>
                  ))}
                  {detail.lines?.length > 0 && (
                    <TableRow className="bg-slate-50 font-semibold" data-testid="payroll-detail-totals">
                      <TableCell>Totals</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUSD(detail.lines.reduce((s, l) => s + (Number(l.base_salary) || 0), 0))}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUSD(detail.lines.reduce((s, l) => s + (Number(l.gross) || 0), 0))}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUSD(detail.lines.reduce((s, l) => s + (Number(l.paye_tax) || 0), 0))}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          {detail && <RecordTrail entity_type="payroll_run" entity_id={detail.id} record={detail} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
//  MASTERS (suppliers, customers, employees)
// ─────────────────────────────────────────────────────────────────────────────
export function MasterPage({ kind, title, extra = [] }) {
  const { user, can } = useAuth();
  // Phase 19 PR-2 hot-fix (2026-07-06) — gate the "+ New" button by the
  // canonical permission key (was a hard-coded role list). The matching key
  // is `operations.{kind}.create` for suppliers/customers/employees.
  const createKey = `operations.${kind}.create`;
  const editKey = `operations.${kind}.edit`;
  const canCreate = can ? can(createKey) : false;
  const canEdit = can ? (can(createKey) || can(editKey)) : false;
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false); const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);  // Phase 15 — track edit mode
  const [selected, setSelected] = useState(null);
  const [txCount, setTxCount] = useState(null);

  // Phase 15 (2026-06-30) — supplier service type dropdown options.
  const SERVICE_TYPES = [
    "Legal Services", "Consultancy Services", "Office Supplies",
    "Rent Services", "Maintenance Services", "IT Services",
    "Transport Services", "Other",
  ];

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/${kind}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  // When a row is selected, fetch related transaction count for the drawer.
  useEffect(() => {
    if (!selected) { setTxCount(null); return; }
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/transactions?counterparty_id=${selected.id}&page_size=1`);
        if (live) setTxCount(data.total ?? (data.items?.length || 0));
      } catch { if (live) setTxCount(0); }
    })();
    return () => { live = false; };
  }, [selected]);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      let result;
      if (editingId) {
        const res = await api.patch(`/${kind}/${editingId}`, form);
        result = res.data;
        toast.success("Updated.");
      } else {
        const res = await api.post(`/${kind}`, form);
        result = res.data;
        // Phase 19 (2026-07-06) — surface pre-approval state in the toast so
        // the user knows the record was saved but is awaiting admin review.
        if (result?.status === "pending_admin_approval") {
          toast.success("Saved — awaiting admin approval.", {
            description: "Visible in the Pending Approvals tab; an admin must approve before it goes live.",
          });
        } else {
          toast.success("Created.");
        }
      }
      setOpen(false); setForm({}); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (it) => {
    // Phase 15 (2026-06-30) — pre-populate form from row & open in edit mode.
    setForm({ ...it });
    setEditingId(it.id);
    setOpen(true);
  };

  return (
    <div className="space-y-5" data-testid={`${kind}-page`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-2xl font-bold">{title}</h2>
        {canCreate && (<Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid={`new-${kind}-button`}><Plus className="h-4 w-4 mr-2" />New {title.replace(/s$/, "")}</Button></DialogTrigger>
          {/* Phase 11 — Modal restructured into fixed header / scrollable body / fixed footer.
              Employees gets a 2-col grid; smaller masters use the original single col. */}
          <DialogContent
            className="flex flex-col p-0 max-w-2xl gap-0 max-h-[90vh]"
            data-testid={`${kind}-create-dialog`}
          >
            <DialogHeader className="px-6 py-4 border-b border-slate-100 shrink-0">
              <DialogTitle>{editingId ? `Edit ${title.slice(0,-1)}` : `New ${title.slice(0,-1)}`}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4" style={{ maxHeight: "60vh" }}>
              <div className={kind === "employees"
                ? "grid grid-cols-1 sm:grid-cols-2 gap-3"
                : "space-y-3"}>
                {["name", "email", "phone", "address", "tax_id", ...extra].map((f) => (
                  <div key={f} className={kind === "employees" && f === "address" ? "sm:col-span-2" : undefined}>
                    <Label>{f.replace(/_/g, " ")}</Label>
                    {kind === "suppliers" && f === "service_type" ? (
                      <Input
                        list="service-type-options"
                        value={form[f] || ""}
                        onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                        placeholder="Pick or type a service"
                        data-testid={`field-${f}`}
                      />
                    ) : (
                      <Input
                        type={f === "base_salary" ? "number" : "text"}
                        value={form[f] || ""}
                        onChange={(e) => setForm({ ...form, [f]: f === "base_salary" ? Number(e.target.value) : e.target.value })}
                        data-testid={`field-${f}`}
                      />
                    )}
                  </div>
                ))}
                {kind === "suppliers" && (
                  <datalist id="service-type-options">
                    {SERVICE_TYPES.map((s) => <option key={s} value={s} />)}
                  </datalist>
                )}
              </div>
            </div>
            <DialogFooter className="px-6 py-3 border-t border-slate-100 shrink-0 bg-white">
              <Button variant="outline" onClick={() => { setOpen(false); setEditingId(null); setForm({}); }}>Cancel</Button>
              <Button onClick={create} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid={`${kind}-create-submit`}>
                {editingId ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>)}
      </div>
      <Card><CardContent className="p-0">
        {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
        : (<div className="overflow-x-auto"><Table><TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead>
          {extra.includes("service_type") && <TableHead>Service</TableHead>}
          {extra.includes("position") && <TableHead>Position</TableHead>}
          {extra.includes("base_salary") && <TableHead className="text-right">Base Salary</TableHead>}
          <TableHead className="hidden md:table-cell">Owner</TableHead>
          <TableHead>Status</TableHead>
          {canEdit && <TableHead className="text-right">Actions</TableHead>}
        </TableRow></TableHeader><TableBody>
          {items.map((it) => (
            <TableRow
              key={it.id}
              data-testid={`${kind}-row-${it.id}`}
              onClick={() => setSelected(it)}
              className="cursor-pointer hover:bg-slate-50 transition-colors"
            >
              <TableCell className="font-medium">{it.name}</TableCell>
              <TableCell>{it.email || "—"}</TableCell><TableCell>{it.phone || "—"}</TableCell>
              {extra.includes("service_type") && <TableCell>{it.service_type || "—"}</TableCell>}
              {extra.includes("position") && <TableCell>{it.position || "—"}</TableCell>}
              {extra.includes("base_salary") && <TableCell className="text-right tabular-nums">{fmtUSD(it.base_salary)}</TableCell>}
              <TableCell className="hidden md:table-cell"><OwnerCell created_by_email={it.created_by_email} created_at={it.created_at} updated_by_email={it.updated_by_email} updated_at={it.updated_at} /></TableCell>
              <TableCell>
                {it.status === "pending_admin_approval"
                  ? <Badge className="bg-amber-100 text-amber-800" data-testid={`${kind}-row-pending-badge`}>Pending Approval</Badge>
                  : (it.is_active
                      ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      : <Badge className="bg-slate-200 text-slate-700">Inactive</Badge>)}
              </TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); startEdit(it); }}
                    title="Edit"
                    data-testid={`${kind}-edit-${it.id}`}
                  >
                    <Pencil className="h-4 w-4 text-amber-600" />
                  </Button>
                </TableCell>
              )}
            </TableRow>))}
          {!items.length && (<TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">None yet.</TableCell></TableRow>)}
        </TableBody></Table></div>)}
      </CardContent></Card>

      {/* Master detail drawer — Phase 8 */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-lg" data-testid={`${kind}-detail-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selected?.name}
              {kind === "suppliers" && selected?.is_consultant && (
                <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px] uppercase">Consultant</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <DetailRow label="Email" value={selected.email} />
              <DetailRow label="Phone" value={selected.phone} />
              <DetailRow label="Address" value={selected.address} />
              <DetailRow label="Tax ID" value={selected.tax_id} />
              {extra.includes("position") && <DetailRow label="Position" value={selected.position} />}
              {extra.includes("department") && <DetailRow label="Department" value={selected.department} />}
              {extra.includes("base_salary") && <DetailRow label="Base salary" value={fmtUSD(selected.base_salary)} />}
              <DetailRow label="Status" value={selected.is_active ? "Active" : "Inactive"} />
              {selected.notes && <DetailRow label="Notes" value={selected.notes} />}
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                <p className="text-xs uppercase text-slate-500">Related transactions</p>
                <p className="text-lg font-semibold text-slate-900 tabular-nums">
                  {txCount === null ? "…" : txCount}
                </p>
                {txCount > 0 && (
                  <Link to={`/transactions?counterparty_id=${selected.id}`}
                        className="text-xs text-[#2F86B5] underline">
                    View transactions →
                  </Link>
                )}
              </div>
              <RecordTrail entity_type={kind.slice(0, -1)} entity_id={selected.id} record={selected} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500 text-xs uppercase tracking-wide">{label}</span>
      <span className="text-slate-800 text-right break-all">{value || "—"}</span>
    </div>
  );
}
